import { computed, signal, untracked } from '@angular/core';

/**
 * Helper for managing editable lists that allow selection etc.
 */
export function selectionManager<T>(
  initialOptions: readonly T[] = [],
  initialSelected: Iterable<T> = []
) {
  const selectedState = signal<ReadonlySet<T>>(new Set());
  const optionsState = signal<readonly T[]>(initialOptions);

  const selected = computed(() => [...selectedState()]);
  const options = optionsState.asReadonly();

  const optionSet = computed(() => new Set(options()));

  const selectionCount = computed(() => selectedState().size);
  const hasSelection = computed(() => selectedState().size > 0);

  const allSelected = computed(() => {
    const currentOptions = options();

    if (currentOptions.length === 0) {
      return false;
    }

    const currentSelection = selectedState();
    return currentOptions.every(option => currentSelection.has(option));
  });

  function isSelected(value: T): boolean {
    return selectedState().has(value);
  }

  function select(value: T): void {
    if (!untracked(optionSet).has(value)) {
      return;
    }

    selectedState.update(current => {
      if (current.has(value)) {
        return current;
      }

      const next = new Set(current);
      next.add(value);
      return next;
    });
  }

  function deselect(value: T): void {
    selectedState.update(current => {
      if (!current.has(value)) {
        return current;
      }

      const next = new Set(current);
      next.delete(value);
      return next;
    });
  }

  function toggle(value: T): void {
    if (!untracked(optionSet).has(value)) {
      return;
    }

    selectedState.update(current => {
      const next = new Set(current);

      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }

      return next;
    });
  }

  function set(values: Iterable<T>): void {
    const allowed = untracked(optionSet);
    const next = new Set<T>();

    for (const value of values) {
      if (allowed.has(value)) {
        next.add(value);
      }
    }

    selectedState.set(next);
  }

  function setOptions(values: readonly T[]): void {
    optionsState.set(values);
    selectedState.set(new Set());
  }

  function addOption(value: T, autoSelect = true): void {
    if (untracked(optionSet).has(value)) {
      return;
    }
    optionsState.update(current => [...current, value]);
    if (autoSelect) select(value);
  }

  function updateOption(value: T, newValue: T): void {
    optionsState.update(current => current.map(item => (item === value ? newValue : item)));
    deselect(value);
    select(newValue);
  }

  function removeOption(value: T): void {
    if (!untracked(optionSet).has(value)) {
      return;
    }

    optionsState.update(current => current.filter(option => option !== value));
    deselect(value);
  }

  function selectAll(): void {
    selectedState.set(new Set(untracked(options)));
  }

  function deselectAll(): void {
    if (untracked(selectedState).size === 0) {
      return;
    }

    selectedState.set(new Set());
  }

  function toggleAll(): void {
    if (untracked(allSelected)) {
      deselectAll();
    } else {
      selectAll();
    }
  }

  set(initialSelected);

  return {
    selected,
    options,

    selectionCount,
    hasSelection,
    allSelected,
    isSelected,

    select,
    deselect,
    toggle,
    set,

    setOptions,
    addOption,
    updateOption,
    removeOption,

    selectAll,
    deselectAll,
    toggleAll,
  };
}
